/* eslint-disable camelcase */

import {
  GcpBox,
  GcpDateRangeRecurring,
  GcpRecurringSelectStatus,
  GcpSearchBox,
  GcpTypography,
  GcpUploadLogTable,
} from '@back-office/ui';
import { Button, Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { CloseOutlined } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import { DateRange } from '@material-ui/pickers';
import { observer } from 'mobx-react';
import React, { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import useViewModel from './viewmodel';

export interface Store {
  date: DateRange<Date>;
  handleOnChangeDatePicker: (date: DateRange<Date>) => void;
  handleOnChangePage: (newPage: number) => void;
  handleOnClear: (event: MouseEvent<HTMLOrSVGElement>) => void;
  handleOnClickDatePicker: () => void;
  handleOnCloseDatePicker: () => void;
  handleOnGo: () => void;
  openCalendar: boolean;
  page: number;
  count: number;
  query: string;
  start: number;
  key: string;
  status: string;
  process_status: string;
  mobile_no: string;
  is_multiple_selected: boolean;
  handleKeySearch: (searchValue: string) => void;
  handleStatusSearch: (value: string) => void;
  handleOnClearMultipleSelectedQuery: () => void;
  handleDownloadFile: (fileName: string) => void;
}

export interface AlertState {
  status: string;
  msg: string;
}

export interface listItem {
  id: string;
  result?: string;
  error?: string;
  error_message: string;
}

export interface responseDataProps {
  approve_success_count: number;
  is_already_approved_count: number;
  is_fail_to_approve_count: number;
  is_overdue_count: number;
  approve_success_list: listItem[] | number;
  is_already_approved_list: listItem[] | number;
  is_fail_to_approve_list: listItem[] | number;
  is_overdue_list: listItem[] | number;
}

export const defaultResponse: responseDataProps = {
  approve_success_count: 0,
  is_already_approved_count: 0,
  is_fail_to_approve_count: 0,
  is_overdue_count: 0,
  approve_success_list: [],
  is_already_approved_list: [],
  is_fail_to_approve_list: [],
  is_overdue_list: [],
};

export const useStyles = makeStyles(() => ({
  alert: {
    justifyContent: 'center',
    margin: '16px 0',
  },
  disableBtn: {
    boxShadow: 'none',
    cursor: 'none',
  },
}));

export interface UploadLogsProps {
  uploaded: boolean;
}

const UploadLogs: FC<UploadLogsProps> = observer((props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { uploaded } = props;
  const {
    state,
    handleApproveRecurringFile,
    handleRejectRecurringFile,
    isMultiSelect,
    auth,
    recurringFile,
    isCheckerPermission,
    handleMultipleSelection,
    handleCloseMultipleSelection,
    alertMessage,
    alertStatus,
    failedAlert,
    tryagainAlert,
    overdueAlert,
    approvedRecurringCheckLists,
  } = useViewModel({ uploaded });

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item alignItems="center" xs={2}>
          <GcpTypography variant="h3">
            {t('payment_file.upload_log')}
          </GcpTypography>
        </Grid>

        <Grid item xs={4}>
          <GcpSearchBox
            handleSearch={state.handleKeySearch}
            label={t('payment_file.upload_logs.search.key')}
            value={state.key}
          />
        </Grid>
        <Grid item xs={3}>
          <GcpDateRangeRecurring
            date={state.date}
            handleOnChange={state.handleOnChangeDatePicker}
            handleOnClear={state.handleOnClear}
            handleOnClick={state.handleOnClickDatePicker}
            handleOnClose={state.handleOnCloseDatePicker}
            openCalendar={state.openCalendar}
          />
        </Grid>
        <Grid item xs={3}>
          <GcpRecurringSelectStatus
            disabled={isMultiSelect}
            value={state.status}
            onChange={state.handleStatusSearch}
          />
        </Grid>
      </Grid>
      {(isCheckerPermission || auth.currentUser.role === 'ADMIN') && (
        <GcpBox
          alignItems="center"
          display="flex"
          gap={2}
          justifyContent="space-between"
          mt={3}
        >
          <Typography color="textPrimary" style={{ color: '#000000A6' }}>
            {isMultiSelect && t('payment_file.multiple_selection_notice')}
          </Typography>

          <GcpBox display="flex" gap={2} justifyContent="flex-end">
            <Button
              color={isMultiSelect ? 'primary' : 'default'}
              size="large"
              type="button"
              variant={isMultiSelect ? 'contained' : 'outlined'}
              onClick={handleMultipleSelection}
            >
              {t('payment_file.multiple_selection')}
            </Button>
            {isMultiSelect && (
              <Button variant="outlined" onClick={handleCloseMultipleSelection}>
                <CloseOutlined />
              </Button>
            )}
          </GcpBox>
        </GcpBox>
      )}

      {alertMessage && (
        <Alert
          className={classes.alert}
          icon={false}
          severity={alertStatus === 'Approved' ? 'success' : 'error'}
        >
          <GcpTypography variant="body1">{alertMessage}</GcpTypography>
        </Alert>
      )}
      {failedAlert.msg && (
        <Alert className={classes.alert} icon={false} severity="error">
          <GcpTypography variant="body1">{failedAlert.msg}</GcpTypography>
        </Alert>
      )}
      {tryagainAlert.msg && (
        <Alert className={classes.alert} icon={false} severity="error">
          <GcpTypography variant="body1">{tryagainAlert.msg}</GcpTypography>
        </Alert>
      )}
      {overdueAlert.msg && (
        <Alert className={classes.alert} icon={false} severity="error">
          <GcpTypography variant="body1">{overdueAlert.msg}</GcpTypography>
        </Alert>
      )}

      <GcpBox mt={3}>
        <GcpUploadLogTable
          count={state.count}
          data={recurringFile.list}
          handleApproveRecurringFile={handleApproveRecurringFile}
          handleCheckListRecurringFile={approvedRecurringCheckLists}
          handleDownloadFile={state.handleDownloadFile}
          handleOnChangePage={state.handleOnChangePage}
          handleRejectRecurringFile={handleRejectRecurringFile}
          isAdmin={auth.currentUser.role === 'ADMIN'}
          isCheckerPermission={isCheckerPermission}
          multiselect={isMultiSelect}
          page={state.page}
          pagination={recurringFile.pagination}
        />
      </GcpBox>
    </>
  );
});

export default UploadLogs;
